import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { Channel, ChannelItem } from 'types/channel';
import {
  getChannel,
  getChannelItems,
  GetChannelResponse,
  GetChannelItemsResponse,
} from '../api/marketplaceApi';

export interface ChannelDetail extends Channel {
  item_count?: number;
  subscriber_count?: number;
}

export interface UseChannelDetailResult {
  channel: ChannelDetail | null;
  items: ChannelItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useChannelDetail(channelId: string | null): UseChannelDetailResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [channel, setChannel] = useState<ChannelDetail | null>(null);
  const [items, setItems] = useState<ChannelItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !channelId) {
      setChannel(null);
      setItems([]);
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const [channelResponse, itemsResponse]: [GetChannelResponse, GetChannelItemsResponse] =
          await Promise.all([
            getChannel(apiClients.schemaClient, channelId),
            getChannelItems(apiClients.schemaClient, channelId),
          ]);

        if (!cancelled) {
          setChannel(channelResponse.channel);
          setItems(itemsResponse.data);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load channel details';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, channelId, fetchTrigger]);

  return { channel, items, loading, error, refetch };
}
